import { msalInstance } from '../main';
import api from './api';
import graphApi from './graphApi';
import { apiScopes } from '../auth/authConfig';

interface GraphUserProfile {
    id: string;
    displayName: string;
    mail: string;
    userPrincipalName: string;
    jobTitle: string;
    department?: string;
    officeLocation?: string;
    businessPhones?: string[];
    mobilePhone?: string;
}

interface UserProfileResponse {
    id: string;
    name: string;
    email: string;
    jobTitle: string;
    photoUrl?: string;
}

// Request cache to prevent duplicate API calls
const requestCache = new Map<string, Promise<any>>();

// Cache helper function
const withCache = <T>(key: string, promiseFactory: () => Promise<T>): Promise<T> => {
    if (requestCache.has(key)) {
        return requestCache.get(key) as Promise<T>;
    }

    const promise = promiseFactory().finally(() => {
        requestCache.delete(key);
    });

    requestCache.set(key, promise);
    return promise;
};

// Authenticate with backend - validates user exists and has permissions
export const fetchUserProfile = async (): Promise<any> => {
    return withCache('fetchUserProfile', async () => {
        const account = msalInstance.getActiveAccount();
        if (!account) throw new Error("No active account");

        try {
            // Get backend API token
            const apiTokenResponse = await msalInstance.acquireTokenSilent({
                scopes: apiScopes.backend,
                account,
            });

            // Get Graph token for user validation
            const graphTokenResponse = await msalInstance.acquireTokenSilent({
                scopes: apiScopes.graph,
                account,
            });

            const response = await api.post('/users/login-with-token/', {}, {
                headers: {
                    Authorization: `Bearer ${apiTokenResponse.accessToken}`,
                    'X-Graph-Token': graphTokenResponse.accessToken,
                },
                timeout: 15000,
            });

            if (response.status === 200) {
                return response.data;
            } else if (response.status === 403) {
                // User not authorized in system
                throw new Error("User not authorized");
            } else if (response.status === 401) {
                // Token issue
                throw new Error("Unauthorized");
            } else {
                throw new Error("Authentication failed");
            }
        } catch (error: any) {
            // Handle 403 Forbidden specifically
            if (error.response?.status === 403) {
                throw new Error("User not authorized to access this application");
            }
            
            // Handle MSAL token expiration
            if (error.name === 'InteractionRequiredAuthError') {
                await msalInstance.loginRedirect({
                    scopes: apiScopes.graph,
                    prompt: 'select_account'
                });
                throw new Error("Token expired, redirecting to login");
            }
            
            throw error;
        }
    });
};

// Fetch complete user profile from Microsoft Graph
export const fetchGraphUserProfile = async (): Promise<UserProfileResponse> => {
    return withCache('fetchGraphUserProfile', async () => {
        const account = msalInstance.getActiveAccount();
        if (!account) throw new Error("No active account");

        try {
            // Fetch user profile from Graph API
            const profileResponse = await graphApi.get('/me');
            const profile: GraphUserProfile = profileResponse.data;
            
            // Fetch user photo (optional, non-blocking)
            const photoUrl = await getUserPhoto().catch(() => null);

            return {
                id: profile.id,
                name: profile.displayName,
                email: profile.mail || profile.userPrincipalName,
                jobTitle: profile.jobTitle || 'User',
                photoUrl: photoUrl || undefined,
            };
        } catch (error: any) {
            console.error('Error fetching Graph user profile:', error);
            
            // Return basic account info as fallback
            return {
                id: account.localAccountId,
                name: account.name || 'User',
                email: account.username,
                jobTitle: (account.idTokenClaims as any)?.jobTitle || 'User',
            };
        }
    });
};

// Fetch user photo from Microsoft Graph (non-critical)
export const getUserPhoto = async (): Promise<string | null> => {
    return withCache('getUserPhoto', async () => {
        try {
            const response = await graphApi.get('/me/photo/$value', {
                responseType: 'blob',
                timeout: 5000,
            });
            
            // Convert blob to data URL instead of blob URL
            return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(response.data);
            });
        } catch (error) {
            console.debug('User photo not available:', error);
            return null;
        }
    });
};